/**
 * What the report page's header can do: rename, save a copy, delete, publish,
 * and share with people. Each change refreshes the report and the list.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { apiRequest } from '../../lib/apiClient.js';
import { queryKeys } from '../../lib/queryKeys.js';

const createdSchema = z.object({ id: z.string() }).passthrough();
const candidateSchema = z.object({
  userId: z.string(),
  name: z.string().nullable(),
  email: z.string(),
  shared: z.boolean(),
});
export type ShareCandidate = z.infer<typeof candidateSchema>;

export const useReportActions = (id: string) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const base = `/api/report-templates/${id}`;
  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.reportTemplate(id) }),
      queryClient.invalidateQueries({ queryKey: queryKeys.reportTemplates() }),
    ]);

  const rename = useMutation({
    mutationFn: (name: string) => apiRequest<unknown>(base, { method: 'PATCH', body: { name } }),
    onSuccess: refresh,
  });

  // Save as my own: the copy is a new report, so open it.
  const copy = useMutation({
    mutationFn: async () =>
      createdSchema.parse(await apiRequest<unknown>(`${base}/copy`, { method: 'POST' })),
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.reportTemplates() });
      void navigate(`/reports/templates/${created.id}`);
    },
  });

  const remove = useMutation({
    mutationFn: () => apiRequest<unknown>(base, { method: 'DELETE' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.reportTemplates() });
      void navigate('/reports');
    },
  });

  const publish = useMutation({
    mutationFn: (on: boolean) =>
      apiRequest<unknown>(`${base}/publish`, { method: on ? 'POST' : 'DELETE' }),
    onSuccess: refresh,
  });

  const share = useMutation({
    mutationFn: ({ userId, on }: { userId: string; on: boolean }) =>
      on
        ? apiRequest<unknown>(`${base}/shares`, { method: 'POST', body: { userId } })
        : apiRequest<unknown>(`${base}/shares/${userId}`, { method: 'DELETE' }),
    onSuccess: () =>
      Promise.all([
        refresh(),
        queryClient.invalidateQueries({ queryKey: queryKeys.reportShareCandidates(id) }),
      ]),
  });

  return { rename, copy, remove, publish, share };
};

/** People the owner can share with: everyone in the tenant with report access, minus the owner. */
export const useShareCandidates = (id: string, enabled: boolean) =>
  useQuery({
    queryKey: queryKeys.reportShareCandidates(id),
    queryFn: async () =>
      z
        .array(candidateSchema)
        .parse(await apiRequest<unknown>(`/api/report-templates/${id}/share-candidates`)),
    enabled,
  });
